import { useGetAnalysis } from "Infrastructure/ApiZfp/AnalyzerApi";
import { type AnalysisVersionInfo, AnalysisVersionInfoState } from "Infrastructure/ApiZfp/Schema";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useContext, useState } from "react";
import { AnalysisDetailContext } from "../AnalysisDetailContext";
import { useAnalysisAccess } from "../useAnalysisAccess";
import { useDuplicateVersion } from "./useDuplicateVersion";
import type { SourceVersion } from "./utils";

type Params = {
  readonly isEnabled: boolean;
  readonly isOwner: boolean;
  readonly displayedVersion: AnalysisVersionInfo | undefined;
  readonly latestVersion: AnalysisVersionInfo | undefined;
};

/**
 * What **Duplikovat verzi analýzy** does: an own analysis whose latest version is in progress gets it closed first,
 * after a confirmation (`CloseAndDuplicateDialog`); anything else is duplicated right away (`useDuplicateVersion`).
 */
export const useDuplicateAction = ({ isEnabled, isOwner, displayedVersion, latestVersion }: Params) => {
  const { t } = useResource();
  const { analysisId } = useContext(AnalysisDetailContext);
  const { duplicate, isDuplicating, conflict } = useDuplicateVersion();
  const [closingSourceVersion, setClosingSourceVersion] = useState<SourceVersion | null>(null);

  const mustCloseLatest = isOwner && latestVersion?.state === AnalysisVersionInfoState.in_progress;
  // closing is a write that needs the latest version's edit access, which the displayed version may not reflect
  const { data: latestAnalysis } = useGetAnalysis(analysisId, latestVersion?.versionId ?? "", {
    query: { enabled: isEnabled && mustCloseLatest && !!latestVersion },
  });
  const latestAccess = useAnalysisAccess(latestAnalysis);
  const isHandedOver = mustCloseLatest && !!latestAnalysis && !latestAccess.canChangeState;

  return {
    isDisabled: isDuplicating || (mustCloseLatest && !latestAnalysis) || isHandedOver,
    disabledReason: isHandedOver ? t(Resources.AnalysisDetail.Duplicate.Error.HandedOver) : null,
    start: () => {
      if (!displayedVersion) return;

      if (mustCloseLatest) {
        setClosingSourceVersion(displayedVersion);
      } else {
        duplicate(displayedVersion);
      }
    },
    conflict,
    closingSourceVersion,
    closeClosingDialog: () => setClosingSourceVersion(null),
  };
};
